import { useCallback, useEffect, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { selectExamState, startExam, fetchExamQuestions } from '../../redux/examSlice';
import {
  selectActiveActivity,
  setActivityContent,
  addMessageToCurrentActivity,
} from '../../redux/classroomSlice';
import { selectProfile as selectStudentProfile } from '../../redux/profileSlice';
import { ExaminerService } from '../../services/llmClient/ExaminerService';
import '../shared/ActivityComponent.css';

const Examiner = ({ preloadedContent }) => {
  const dispatch = useDispatch();
  const activeActivity = useSelector(selectActiveActivity);
  const profile = useSelector(selectStudentProfile);
  const examState = useSelector(selectExamState);
  const prevMessageCount = useRef(activeActivity?.messages?.length || 0);
  const prevActivityId = useRef();

  const isActive = activeActivity?.leader === 'Examiner';

  const questions =
    preloadedContent !== undefined ? preloadedContent : activeActivity?.content || [];

  useEffect(() => {
    if (preloadedContent !== undefined) return;

    if (isActive && activeActivity?.id !== prevActivityId.current) {
      prevActivityId.current = activeActivity?.id;
      dispatch(setActivityContent({ content: [] }));
      dispatch(startExam());

      const lessonTitle = activeActivity?.title;
      const courseDetails = activeActivity?.courseDetails || {};

      if (!lessonTitle || !profile) {
        return;
      }

      dispatch(fetchExamQuestions({ lessonTitle, studentProfile: profile, courseDetails }));
    }
  }, [isActive, activeActivity, dispatch, profile, preloadedContent]);

  const messages = useMemo(() => activeActivity?.messages || [], [activeActivity]);
  const currentQuestion = questions[examState.currentQuestionIndex];

  const sendResponse = useCallback(async () => {
    if (!isActive) return;

    const lastMessage = messages[messages.length - 1];
    if (!lastMessage || lastMessage.sender === 'Examiner') return;

    try {
      const response = await ExaminerService.processStudentQuestion(
        lastMessage.text,
        currentQuestion,
        messages,
        profile,
        examState,
        dispatch
      );
      if (response) {
        dispatch(addMessageToCurrentActivity({ message: response }));
      }
    } catch (error) {
      dispatch(
        addMessageToCurrentActivity({
          message: {
            sender: 'Examiner',
            text: "Sorry, I couldn't respond right now.",
            timestamp: new Date().toLocaleTimeString(),
          },
        })
      );
    }
  }, [isActive, dispatch, messages, profile, currentQuestion, examState]);

  useEffect(() => {
    if (!isActive) return;

    const currentMessageCount = messages.length;
    if (currentMessageCount > prevMessageCount.current) {
      const lastMessage = messages[currentMessageCount - 1];
      if (lastMessage?.sender !== 'Examiner') {
        setTimeout(() => {
          sendResponse();
        }, 1000);
      }
    }
    prevMessageCount.current = currentMessageCount;
  }, [isActive, messages, sendResponse]);

  return null;
};
export default Examiner;
